import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import EventBlock from "../../components/EventBlock";
import { format, addDays, isSameDay, isBefore, isAfter } from 'date-fns';
import { getTeamDailySessions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

function DailyView({ startDate, daysToShow, today, selectedMembers, isAdminView }) {
    const [dailySessions, setDailySessions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { user, groupInfo } = useAuth();
    const [maxHeight, setMaxHeight] = useState(722);
    const blockRefs = useRef([]);
    const groupId = groupInfo.groupId;

    useEffect(() => {
        async function fetchSessions() {
            if (!user) return;

            setIsLoading(true);
            try {
                const sessionsPromises = Array.from({ length: daysToShow }, async (_, index) => {
                    const date = addDays(startDate, index);
                    
                    // Skip days that are outside the course date range
                    if (groupInfo) {
                        if (groupInfo.startDate && isBefore(date, groupInfo.startDate)) {
                            return { date: format(date, 'yyyy-MM-dd'), sessions: [] };
                        }
                        if (groupInfo.endDate && isAfter(date, groupInfo.endDate)) {
                            return { date: format(date, 'yyyy-MM-dd'), sessions: [] };
                        }
                    }
                    
                    const sessions = await getTeamDailySessions(user, date, groupId);
                    return { date: format(date, 'yyyy-MM-dd'), sessions };
                });

                const results = await Promise.all(sessionsPromises);
                const sessionsMap = results.reduce((acc, { date, sessions }) => {
                    acc[date] = sessions;
                    return acc;
                }, {});

                setDailySessions(sessionsMap);
            } catch (error) {
                console.error("Error fetching sessions:", error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchSessions();
    }, [user, startDate, daysToShow, groupInfo]);

    useEffect(() => {
        const heights = blockRefs.current.map(ref => ref?.offsetHeight || 0);
        const maxBlockHeight = Math.max(...heights);
        setMaxHeight(maxBlockHeight);
    }, [dailySessions]);

    const handleSessionsUpdate = useCallback((date, updatedSessions, originalDate = null) => {
        setDailySessions(prevSessions => {
            const newSessions = { ...prevSessions };
            if (originalDate && date !== originalDate) {
                newSessions[originalDate] = (prevSessions[originalDate] || [])
                    .filter(session => !updatedSessions.some(us => us.id === session.id));

                newSessions[date] = [
                    ...(prevSessions[date] || []),
                    ...updatedSessions
                ];
            } else {
                const existingSessions = prevSessions[date] || [];
                const updatedSessionIds = updatedSessions.map(session => session.id);
                const remainingSessions = existingSessions.filter(
                    session => !updatedSessionIds.includes(session.id)
                );
                newSessions[date] = [
                    ...remainingSessions,
                    ...updatedSessions
                ];
            }
            return newSessions;
        });
    }, []);

    const filterSessionsByContributor = useCallback((sessions) => {
        if (selectedMembers.length === 0) return sessions;  //no one selected
        return sessions.filter(session =>
                session.contributors && session.contributors.some(contributor =>
                    selectedMembers.some(member => member.email === contributor.email)
                )
        );
    }, [selectedMembers]);

    return (
        <BlockWrapper>
            {Array.from({ length: daysToShow }, (_, index) => {
                const date = addDays(startDate, index);
                const fullDate = format(date, 'yyyy-MM-dd');
                
                // Check if date is outside course range
                const isOutsideRange = groupInfo && (
                    (groupInfo.startDate && isBefore(date, groupInfo.startDate)) ||
                    (groupInfo.endDate && isAfter(date, groupInfo.endDate))
                );
                
                const filteredSessions = filterSessionsByContributor(dailySessions[fullDate] || []);
                return (
                    <EventBlock
                        key={fullDate}
                        ref={el => blockRefs.current[index] = el}
                        date={date}
                        isToday={isSameDay(date, today)}
                        sessions={filteredSessions}
                        onSessionsUpdate={handleSessionsUpdate}
                        maxHeight={maxHeight}
                        viewType="day"
                        isOutsideRange={isOutsideRange}
                        isAdminView={isAdminView}
                    />
                );
            })}
        </BlockWrapper>
    );
}

const BlockWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow-x: auto;
    gap: 20px
`;

export default DailyView;